import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const GOLD = '#C8922A';
const BD2 = '#EAE6DF';
const BG = '#FAF8F5';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const RED = '#C0392B';
const GRN = '#2E7D4F';

// const CAT_EMOJI = { watch: '⌚', jewelry: '💍', bag: '👜', other: '📦' };
// 兜底，仅当后端 BOC 数据缺失时使用
const FALLBACK_JPY_CNY_RATE = 0.043;

function formatAmount(val) {
  if (val == null) return '--';
  return '¥ ' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function DepositDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rateInfo, setRateInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [balRes, ledgerRes, rateRes] = await Promise.all([
          api.get('/deposits/balance'),
          api.get('/deposits/ledger'),
          // BOC 中行现汇卖出价（公开端点）
          api.get('/exchange-rates/realtime/latest').catch(() => null),
        ]);
        setBalance(balRes.data.data || balRes.data || {});
        setLedger(ledgerRes.data.data || ledgerRes.data || []);
        if (rateRes?.data) setRateInfo(rateRes.data);
      } catch (err) {
        console.error('Failed to fetch deposit data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
  }

  const bal = balance || {};
  const frozenItems = bal.frozen_items || [];

  const getAmountDisplay = (item) => {
    if (item.type === 'deposit' || item.type === 'unfreeze') {
      return { text: item.type === 'deposit' ? `+${formatAmount(item.amount)}` : `解 +${formatAmount(item.amount)}`, color: GRN };
    }
    if (item.type === 'freeze') {
      return { text: `冻 ${formatAmount(item.amount)}`, color: GOLD };
    }
    if (item.type === 'payment') {
      return { text: formatAmount(item.amount), color: RED };
    }
    return { text: formatAmount(item.amount), color: INK2 };
  };

  const getBalanceLabel = (item) => {
    if (item.type === 'freeze') return `可用 ${formatAmount(item.balance_after)}`;
    return `余额 ${formatAmount(item.balance_after)}`;
  };

  return (
    <div style={{ fontFamily: 'var(--sans,sans-serif)', background: BG, color: INK, fontSize: 14, minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '28px 24px' }}>
        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <button onClick={() => navigate(-1)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: `1px solid ${BD2}`, borderRadius: 3,
            padding: '8px 16px', fontSize: 13, color: INK3, cursor: 'pointer',
            transition: 'all .15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = BD2; e.currentTarget.style.color = INK3; }}
          >
            ← 返回
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigate('/deposit')} style={{
              padding: '8px 16px', borderRadius: 3, border: 'none',
              background: GOLD, color: '#fff', fontSize: 13, cursor: 'pointer',
              transition: 'all .15s',
            }}>{t('deposit.recharge')}</button>
          </div>
        </div>

        {/* Balance overview */}
        <div style={{
          background: INK, borderRadius: 6, padding: '28px 32px', marginBottom: 20,
          position: 'relative',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24 }}>
          {(() => {
            const cnyRate = rateInfo?.rate ? Number(rateInfo.rate) : FALLBACK_JPY_CNY_RATE;
            const totalCny = (Number(bal.available_cny) || 0) + (Number(bal.frozen_cny) || 0);
            return [
              { label: t('deposit.balance'), val: formatAmount(totalCny), color: '#fff', sub: `约 ${formatAmount(Math.round(totalCny / cnyRate))} JPY` },
              { label: t('deposit.frozen'), val: formatAmount(bal.frozen_cny), color: '#F0C060', sub: `${frozenItems.length} 笔委托冻结中` },
              { label: t('deposit.available'), val: formatAmount(bal.available_cny), color: '#5FC98A', sub: t('deposit.available') },
            ];
          })().map((item, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', letterSpacing: 2, fontFamily: 'monospace', marginBottom: 8 }}>
                {item.label}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 600, color: item.color }}>
                {item.val}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 4 }}>{item.sub}</div>
            </div>
          ))}
          </div>
          {/* 实时汇率小字 */}
          {rateInfo && (
            <div style={{ marginTop: 12, textAlign: 'center', fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,.35)' }}>
              中行实时汇率 1 JPY = {Number(rateInfo.rate).toFixed(6)} CNY
              {rateInfo.recorded_at && (
                <span style={{ marginLeft: 6 }}>
                  · {new Date(rateInfo.recorded_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false }).slice(5, 16)}
                </span>
              )}
            </div>
          )}
          {/* Withdraw button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={{
              padding: '7px 20px', borderRadius: 3,
              border: '1px solid rgba(255,255,255,.2)', background: 'transparent',
              color: 'rgba(255,255,255,.6)', fontFamily: 'monospace', fontSize: 11,
              letterSpacing: 1, cursor: 'pointer', transition: 'all .15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.4)'; e.currentTarget.style.color = 'rgba(255,255,255,.8)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.2)'; e.currentTarget.style.color = 'rgba(255,255,255,.6)'; }}
            >{t('deposit.refund')}</button>
          </div>
        </div>

        {/* Frozen detail */}
        <div style={{
          background: '#fff', border: `1px solid ${BD2}`, borderRadius: 6,
          marginBottom: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(26,22,18,.06)',
        }}>
          <div style={{
            padding: '14px 20px', borderBottom: `1px solid ${BD2}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{t('deposit.detail')}</span>
            <span style={{ fontSize: 12, color: INK3 }}>
              保证金按「授权价 x 服务费率」冻结，竞拍结束后自动解冻或转为尾款
            </span>
          </div>
          {frozenItems.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: INK4 }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>🔓</div>
              <div>{t('common.noData')}</div>
            </div>
          ) : (
            frozenItems.map((item, i) => (
              <div key={item.id || i} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
                borderBottom: i < frozenItems.length - 1 ? `1px solid ${BD2}` : 'none',
              }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{item.emoji || '📦'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>{item.lot_title}</div>
                  <div style={{ fontSize: 11, color: INK3 }}>{item.meta}</div>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 20, marginTop: 4, display: 'inline-block',
                    ...(item.status === 'bidding'
                      ? { background: 'rgba(200,146,42,.1)', color: GOLD }
                      : { background: 'rgba(26,22,18,.06)', color: INK3 }),
                  }}>{item.status_label}</span>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 600, color: GOLD }}>
                    {formatAmount(item.amount)}
                  </div>
                  <div style={{ fontSize: 11, color: INK4 }}>{item.formula}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Ledger */}
        <div style={{
          background: '#fff', border: `1px solid ${BD2}`, borderRadius: 6,
          overflow: 'hidden', boxShadow: '0 2px 8px rgba(26,22,18,.06)',
        }}>
          <div style={{
            padding: '14px 20px', borderBottom: `1px solid ${BD2}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{t('deposit.history')}</span>
            <span style={{ fontSize: 12, color: INK3 }}>近 30 天</span>
          </div>
          {ledger.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: INK4 }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>📃</div>
              <div>{t('common.noData')}</div>
            </div>
          ) : (
            ledger.map((item, i) => {
              const display = getAmountDisplay(item);
              return (
                <div key={item.id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '13px 20px',
                  borderBottom: i < ledger.length - 1 ? `1px solid ${BD2}` : 'none',
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: item.dot_color || INK4,
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: INK3 }}>{item.meta}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: display.color }}>
                      {display.text}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: INK4, marginTop: 2 }}>
                      {getBalanceLabel(item)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// End of DepositDetail
